import { taskEither } from 'fp-ts/lib/TaskEither';
import { sequenceT } from 'fp-ts/lib/Apply';
import { RouteName, router } from '@/ui/routes';
import Vue, { VNode } from 'vue';
import { ToastOptions } from 'vue-toasted';
import { NamedLocation } from '../routes/types';
import { SelectOption, SelectOptions, SelectOptionsWithTooltip } from '../types';
import { pipe } from 'fp-ts/lib/pipeable';
import { getOrElse, tryCatch } from 'fp-ts/lib/Option';
import { appStoreBadge, googlePlayStoreBadge, ImagelessLinkProps } from '@/ui/components/onboarding/StoreImageLink';
// @ts-ignore
import is from 'is_js';
import { TabQuery, encodeTabQuery } from '@/services/api/resources/analytics/query';
import { EntityFolderList } from '@/entities/entityFolder/model';
import { separator } from '@/ui/pages/analytics/codec';
import { RawLocation } from 'vue-router';
import { FormItemFilter } from '@/services/api/resources/analytics-builder/model';
import { t } from '@/ui/i18n/i18n';
import { Route } from 'vue-router/types/router';

export const sequence = sequenceT(taskEither);

export const pushRoute = (
  location: NamedLocation,
  onComplete?: (route: Route) => void,
) => router.push(location, onComplete);

export const goBack = () => {
  router.go(-1);
};

export const openNewTab = (href = '', internal = true) => {
  if (href) {
    if (internal) {
      href = process.env.VUE_APP_ADMIN_URL + href;
    }

    window.open(href, '_blank');
  }
};

export const openNewRouterTab = (location: RawLocation) => {
  const resolved = router.resolve(location);
  openNewTab(resolved.href);
};

export interface IdProps {
  id: number;
}

export const download = (url: URL) => {
  const link = document.createElement('a');
  link.href = url.toString();
  link.target = '_blank';
  link.click();
};

export const containsAttachment = ({ headers }: Response) => {
  const disposition = headers.get('Content-Disposition');
  return disposition && disposition.indexOf('attachment') !== -1;
};

export const extractFilenameFromDisposition = (disposition: string): string => {
  let filename = '';
  const filenameRegex = /filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/;
  const matches = filenameRegex.exec(disposition);
  if (matches != null && matches[1]) {
    filename = matches[1].replace(/['"]/g, '');
  }
  return filename;
};

interface Formatter<T> {
  (input: T): string;
}

export const enumToSelectOption = <T>(
  formatter: Formatter<T>,
) => (input: T): SelectOption<T> => ({
  value: input,
  label: formatter(input),
});

const inlineProperty = <T extends { data: D }, D extends { [key: string]: string }>(
  property: keyof D,
) => (
  entities: T[],
) => entities
  .map(entity => entity.data[property])
  .join(', ');

export const inlineTitles = inlineProperty('title');
export const inlineNames = inlineProperty('name');

// it can really be any type value
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dataProp = <T extends { data: D }, D extends Record<string, any>>(
  property: keyof D,
) => (
  input: T,
) => input.data[property] || t('common.no-title');

export const dataTitleProp = dataProp('title');
export const dataNameProp = dataProp('name');

export const filterListToOptions = <T = number>({ data }: { data: BaseFilterEntity[] }, emptyTitle = ''): SelectOptions<T> => data.map(
  item => ({
    value: item.id as unknown as T,
    label: item.title || emptyTitle,
  }),
);

export const filterSimpleListToOptions = <T = number>(list : BaseFilterEntity[], emptyTitle = ''): SelectOptions<T> => list.map(
  item => ({
    value: item.id as unknown as T,
    label: item.title || emptyTitle,
  }),
);

export const filterEntityFolderToOptions = (items: EntityFolderList): SelectOptions<string> => items.data.map(
  item => ({
    value: item.data.entityItems.map(entityItem => entityItem.id).join(separator),
    label: item.data.title,
  }),
);

export const filterFormItemToOptions = (
  items: FormItemFilter,
  makeTooltipFn: (value: string) => string = value => value,
): SelectOptionsWithTooltip<string> => items.data.map(
  item => ({
    value: item.id,
    label: item.title,
    tooltip: makeTooltipFn(item.checklistTitle),
  }),
);

export const filterOptions = (options: SelectOptions, parentOptions: number[]): SelectOptions => {
  return parentOptions.length
    ? options.filter(item => parentOptions.includes(item.value))
    : options;
};

export function getMappedRecord<T extends string | number | symbol>(map: Record<T, string>, value: T): string {
  return pipe(
    tryCatch(() => map[value]),
    getOrElse<string>(() => ''),
  );
}

//to debug pipes and flows
export const trace = (value: unknown) => {
  console.info(value);
  console.trace();
  return value;
};

export const when = (condition: boolean, node: VNode) => {
  if (condition) {
    return [node];
  }
  return [];
};

export function operatingSystemToStoreBadge(props: ImagelessLinkProps = {}) {
  if (is.android()) {
    return googlePlayStoreBadge(props);
  }

  if (is.ios()) {
    return appStoreBadge(props);
  }

  return null;
}

export function iosLink() {
  return process.env.VUE_APP_IOS_APP_URL;
}

export function androidLink() {
  return process.env.VUE_APP_ANDROID_APP_URL;
}

export function appStoreLink() {
  if (is.android()) {
    return androidLink();
  }

  return iosLink();
}

export function isMobile() {
  return is.mobile();
}

export const screenWidth = window.screen.width || window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
export const screenHeight = window.screen.height || window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

/* eslint-disable prefer-destructuring */
export const toasted = Vue.toasted;

export const toastAndRedirect = (message: string, routeName: RouteName, queryString?: TabQuery) => {
  toasted.success(message);
  if (queryString) {
    pushRoute({ name: routeName, query: encodeTabQuery(queryString) });
  } else {
    pushRoute({ name: routeName });
  }
};

export const toastWithClickOutside = (message: string, options: ToastOptions = {}) => {
  const clickOutsideHandler = (e: MouseEvent) => {
    if ((e.target as Element)?.closest('.toasted')) {
      return;
    }

    toast.goAway(0);
  };

  document.body.addEventListener('click', clickOutsideHandler);

  const toast = toasted.show(message, options);

  onDomNodeRemoved(toast.el, () => document.body.removeEventListener('click', clickOutsideHandler));
};

export const scrollToElementById = (id?: string) => {
  if (!id) {
    return;
  }

  const element = document.getElementById(id);
  const yOffset = 75;
  const topPos = element ? element.getBoundingClientRect().top - yOffset : undefined;

  window.scrollTo({
    top: topPos, // scroll so that the element is at the top of the view
    behavior: 'smooth', // smooth scroll
  });
};

export const scrollToTop = (element: Element | Window = window) => {
  element.scrollTo(0, 0);
};

export const scrollToBottom = (element: Element | Window = window) => {
  const scrollHeight = element === window
    ? document.documentElement.scrollHeight
    : (element as Element).scrollHeight;

  element.scrollTo({
    top: scrollHeight,
    behavior: 'smooth',
  });
};

export const scrollToBottomDelayed = (delay = 100) => {
  setTimeout(() => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth',
    });
  }, delay);
};

export const reload = (param: { key: string; value: string} | null) => {
  const url = new URL(window.location.href);
  if (param) {
    url.searchParams.set(param.key, String(param.value));
  }
  window.location.href = url.toString();
};

/* eslint-disable prefer-destructuring */
export const fullStory = Vue.fullStory;

/* eslint-disable prefer-destructuring */
export const gtm = Vue.gtm;

// @ts-ignore
export const gtag = Vue.$gtag;

export type VDataComponents<P> = Vue.VNodeData & { props: P };

export const isNestedErrorForField = (error: string, name: string) => new RegExp(`${name}.d*`, 'i').test(error);

export const onDomNodeRemoved = (el: Element, cb: Function): void => {
  if (el.parentElement === null) {
    console.error('Trying to observe an element without parent', el);
    return;
  }

  const observer = new MutationObserver(records => records[0].removedNodes[0] === el && cb());

  observer.observe(el.parentElement, { childList: true });
};
